import { useCallback, useEffect, useState } from 'react';
import { Button, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { useBatchId } from '../../../hooks/useBatchId';
import {
	ApplicationSummary,
	CandidateListData,
	LocationState,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import LeftArrowBlack from '../../../assets/images/arrow-left.svg';
import Icon from '../../../components/ui-components/Icon';
import {
	FUNCTIONARY_HOME,
	INTERVIEW_LANDING,
	SELECT_MODULE,
} from '../../../shared/Routes';
import { useScreeningDashboard } from './useScreeningDashboard';
import FilterIcon from '../../../assets/images/filter-icon.svg';
import { FilterDashboard } from './FilterDashboard';
import ProposerIcon from '../../../assets/images/proposer-icon.svg';
import ProposerIconGrey from '../../../assets/images/proposer-icon-grey.svg';
import WaitingApprover from '../../../assets/images/waiting-approver.svg';
import WaitingApproverGrey from '../../../assets/images/waiting-approver-grey.svg';
import ApprovedCandidates from '../../../assets/images/approved-candidates.svg';
import ApprovedCandidatesGrey from '../../../assets/images/approved-candidates-grey.svg';
import ProposerRecommendation from '../../../assets/images/proposer-recommendation.svg';
import ProposerRecommendationGrey from '../../../assets/images/proposer-recommendation-grey.svg';
import NotGoingForward from '../../../assets/images/not-going-forward.svg';
import NotGoingForwardGrey from '../../../assets/images/not-going-forward-grey.svg';
import { UndoOutlined } from '@ant-design/icons';
import { useApplicationsList } from '../ApplicationsList/useApplicationsList';
import { DashboardList } from './DashboardList';
import {
	ApplicationStatus,
	FROM_SCREENING_DASHBOARD,
} from '../../../shared/Constants';
import { useApplicationId } from '../../../hooks/useApplicationId';
import { VerifyResultsModal } from '../ApplicationsList/VerifyResultsModal';
import { useLocation } from 'react-router-dom';
import { useRedirectToLogin } from '../../../hooks/useRedirectToLogin';
import moment from 'moment';
import { useSnapshot } from 'valtio';
import { BatchInfoState } from '../../../components/BatchInfo.store';

const { Text } = Typography;

export const ScreeningDashboard = () => {
	const { t, i18n } = useTranslation();
	const isLengthyLanguage = i18n.language === 'ta' || i18n.language === 'ma';
	const { role } = useRedirectToLogin();
	const { navigateWithBatchId } = useBatchId();
	const batchStateSnap = useSnapshot(BatchInfoState);
	const batchInfo = batchStateSnap.batchInfo;
	const [dashboardData, setDashboardData] = useState<CandidateListData[]>([]);
	const { adminDashboardResponse, dashboardFilter, onDashboardFilterChange } =
		useScreeningDashboard();
	const { navigateWithApplicationId } = useApplicationId();
	const { applicationsList, onPageChange, paging, onFilterChange, filter } =
		useApplicationsList({});
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
	const [isFilterApplied, setIsFilterApplied] = useState(false);
	const [openResultsModal, setOpenResultsModal] = useState(false);
	const [applicationToVerify, setApplicationToVerify] =
		useState<ApplicationSummary>();
	const [overallCandidates, setOverAllCandidates] = useState<boolean>(true);
	const [isZCUnpublishedRecords, setIsZCUnpublishedRecords] =
		useState<boolean>(false);
	const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(0);
	const location = useLocation();
	const isAdmin = role === UserRole.ADMIN;
	const isZC = role === UserRole.ZC;
	const isApprover = role === UserRole.APPROVER;
	const state = location.state as LocationState | null;

	const screeningAdminRole =
		state?.from ??
		(isAdmin ? undefined : sessionStorage.getItem('screeningAdmin'));

	const isValidDate = (dateStr?: string) =>
		dateStr &&
		dateStr !== '1970-01-01T00:00:00Z' &&
		moment().isSameOrAfter(dateStr);

	useEffect(() => {
		setIsFilterApplied(!isEmpty(dashboardFilter));
	}, [dashboardFilter]);

	useEffect(() => {
		onFilterChange({
			status: [
				ApplicationStatus.FUNCTIONARY_ASSIGNED,
				ApplicationStatus.FUNCTIONARY_SUBMITTED,
				ApplicationStatus.WAITING_FOR_CARE_TEAM,
				ApplicationStatus.APPROVED,
				ApplicationStatus.REJECTED,
				ApplicationStatus.USER_SUBMITTED_APPLICATION,
			],
		});
		setSelectedCardIndex(0);
	}, [onFilterChange, setSelectedCardIndex]);

	const handleEvaluateClick = useCallback(
		(application: ApplicationSummary) => {
			navigateWithApplicationId(INTERVIEW_LANDING, application.applicationId, {
				state: { from: FROM_SCREENING_DASHBOARD },
			});
			sessionStorage.setItem('interviewOrigin', FROM_SCREENING_DASHBOARD);
		},
		[navigateWithApplicationId]
	);

	const handleVerifyResultsClick = useCallback(
		(application: ApplicationSummary) => {
			setApplicationToVerify(application);
			setOpenResultsModal(true);
		},
		[]
	);

	const handleCardClick = (index: number) => {
		setSelectedCardIndex(index);
		const selectedCard = dashboardData[index];

		if (selectedCard) {
			if (selectedCard.id !== 'overallCandidateApplications') {
				setOverAllCandidates(false);
			} else {
				setOverAllCandidates(true);
			}

			if (
				isZC &&
				(selectedCard.id === 'waitingForCareTeam' ||
					selectedCard.id === 'approvedCandidates' ||
					selectedCard.id === 'notGoingForwardCandidates')
			) {
				setIsZCUnpublishedRecords(true);
			} else {
				setIsZCUnpublishedRecords(false);
			}
		}

		if (!selectedCard) return;

		const {
			regionIds,
			zoneNames,
			applicantSrcmId,
			searchByApplicantName,
			searchByProposerName,
			searchByProposerSrcmId,
		} = filter || {};

		const isApplicationFiltered = [
			regionIds,
			zoneNames,
			applicantSrcmId,
			searchByApplicantName,
			searchByProposerName,
			searchByProposerSrcmId,
		].some(Boolean);

		switch (selectedCard.id) {
			case 'proposerYetToBeAssigned': {
				if (isApplicationFiltered) {
					onFilterChange({
						regionIds,
						zoneNames,
						applicantSrcmId,
						searchByApplicantName,
						searchByProposerName,
						searchByProposerSrcmId,
						status: [ApplicationStatus.USER_SUBMITTED_APPLICATION],
					});
				} else {
					onFilterChange({
						status: [ApplicationStatus.USER_SUBMITTED_APPLICATION],
					});
				}
				break;
			}
			case 'waitingForProposer': {
				if (isApplicationFiltered) {
					onFilterChange({
						regionIds,
						zoneNames,
						applicantSrcmId,
						searchByApplicantName,
						searchByProposerName,
						searchByProposerSrcmId,
						status: [ApplicationStatus.FUNCTIONARY_ASSIGNED],
					});
				} else {
					onFilterChange({
						status: [ApplicationStatus.FUNCTIONARY_ASSIGNED],
					});
				}
				break;
			}
			case 'proposerProvidedRecommendation': {
				if (isApplicationFiltered) {
					onFilterChange({
						regionIds,
						zoneNames,
						applicantSrcmId,
						searchByApplicantName,
						searchByProposerName,
						searchByProposerSrcmId,
						status: [ApplicationStatus.FUNCTIONARY_SUBMITTED],
					});
				} else {
					onFilterChange({
						status: [ApplicationStatus.FUNCTIONARY_SUBMITTED],
					});
				}
				break;
			}
			case 'waitingForCareTeam': {
				if (isApplicationFiltered) {
					onFilterChange({
						regionIds,
						zoneNames,
						applicantSrcmId,
						searchByApplicantName,
						searchByProposerName,
						searchByProposerSrcmId,
						status: [ApplicationStatus.WAITING_FOR_CARE_TEAM],
					});
				} else {
					onFilterChange({
						status: [ApplicationStatus.WAITING_FOR_CARE_TEAM],
					});
				}
				break;
			}
			case 'approvedCandidates': {
				if (isApplicationFiltered) {
					onFilterChange({
						regionIds,
						zoneNames,
						applicantSrcmId,
						searchByApplicantName,
						searchByProposerName,
						searchByProposerSrcmId,
						status: [ApplicationStatus.APPROVED],
					});
				} else {
					onFilterChange({
						status: [ApplicationStatus.APPROVED],
					});
				}
				break;
			}
			case 'notGoingForwardCandidates': {
				if (isApplicationFiltered) {
					onFilterChange({
						regionIds,
						zoneNames,
						applicantSrcmId,
						searchByApplicantName,
						searchByProposerName,
						searchByProposerSrcmId,
						status: [ApplicationStatus.REJECTED],
					});
				} else {
					onFilterChange({
						status: [ApplicationStatus.REJECTED],
					});
				}
				break;
			}
			default: {
				if (isApplicationFiltered) {
					onFilterChange({
						regionIds,
						zoneNames,
						applicantSrcmId,
						searchByApplicantName,
						searchByProposerName,
						searchByProposerSrcmId,
						status: [
							ApplicationStatus.FUNCTIONARY_ASSIGNED,
							ApplicationStatus.FUNCTIONARY_SUBMITTED,
							ApplicationStatus.WAITING_FOR_CARE_TEAM,
							ApplicationStatus.APPROVED,
							ApplicationStatus.REJECTED,
							ApplicationStatus.USER_SUBMITTED_APPLICATION,
						],
					});
				} else {
					onFilterChange({
						status: [
							ApplicationStatus.FUNCTIONARY_ASSIGNED,
							ApplicationStatus.FUNCTIONARY_SUBMITTED,
							ApplicationStatus.WAITING_FOR_CARE_TEAM,
							ApplicationStatus.APPROVED,
							ApplicationStatus.REJECTED,
							ApplicationStatus.USER_SUBMITTED_APPLICATION,
						],
					});
				}
			}
		}
	};

	const handleBackFunctionaryClick = useCallback(() => {
		if (screeningAdminRole === 'Screening Admin') {
			navigateWithBatchId(SELECT_MODULE);
			sessionStorage.removeItem('screeningAdmin');
		} else {
			if (isAdmin) {
				onFilterChange({
					status: [ApplicationStatus.FUNCTIONARY_ASSIGNED],
				});
			} else if (isApprover) {
				onFilterChange({
					status: [ApplicationStatus.FUNCTIONARY_SUBMITTED],
				});
			} else if (isZC && isValidDate(batchInfo?.snpResultDateForZC)) {
				onFilterChange({
					status: [ApplicationStatus.APPROVED],
				});
			} else {
				onFilterChange({
					status: [
						ApplicationStatus.FUNCTIONARY_ASSIGNED,
						ApplicationStatus.FUNCTIONARY_SUBMITTED,
					],
				});
			}

			navigateWithBatchId(FUNCTIONARY_HOME);
		}
	}, [
		screeningAdminRole,
		navigateWithBatchId,
		onFilterChange,
		isAdmin,
		isZC,
		isApprover,
		batchInfo?.snpResultDateForZC,
	]);

	const getDashboardData = useCallback(() => {
		if (adminDashboardResponse) {
			const refinedResponseData: CandidateListData[] = [
				{
					id: 'overallCandidateApplications',
					count: adminDashboardResponse.overallCandidateApplication || 0,
					description: t('overallCandidateApplications'),
					imageSrc:
						(adminDashboardResponse.overallCandidateApplication || 0) > 0
							? ProposerIcon
							: ProposerIconGrey,
				},
				{
					id: 'proposerYetToBeAssigned',
					count: adminDashboardResponse.proposerYetToBeAssigned || 0,
					description: t('proposerYetToBeAssigned'),
					imageSrc:
						(adminDashboardResponse.proposerYetToBeAssigned || 0) > 0
							? WaitingApprover
							: WaitingApproverGrey,
				},
				{
					id: 'waitingForProposer',
					count: adminDashboardResponse.waitingForProposer || 0,
					description: t('waitingForProposerReview'),
					imageSrc:
						(adminDashboardResponse.waitingForProposer || 0) > 0
							? WaitingApprover
							: WaitingApproverGrey,
				},
				{
					id: 'proposerProvidedRecommendation',
					count: adminDashboardResponse.proposerProvidedRecommendation || 0,
					description: t('proposerProvidedRecommendation'),
					imageSrc:
						(adminDashboardResponse.proposerProvidedRecommendation || 0) > 0
							? ProposerRecommendation
							: ProposerRecommendationGrey,
				},
				{
					id: 'waitingForCareTeam',
					count: adminDashboardResponse.waitingForCareTeam || 0,
					description: t('waitingForCareTeamReview'),
					imageSrc:
						(adminDashboardResponse.waitingForCareTeam || 0) > 0
							? WaitingApprover
							: WaitingApproverGrey,
				},
				{
					id: 'approvedCandidates',
					count: adminDashboardResponse.approvedCandidates || 0,
					description: t('approvedCandidates'),
					imageSrc:
						(adminDashboardResponse.approvedCandidates || 0) > 0
							? ApprovedCandidates
							: ApprovedCandidatesGrey,
				},
				{
					id: 'notGoingForwardCandidates',
					count: adminDashboardResponse.notGoingForwardCandidates || 0,
					description: t('notGoingForwardCandidates'),
					imageSrc:
						(adminDashboardResponse.notGoingForwardCandidates || 0) > 0
							? NotGoingForward
							: NotGoingForwardGrey,
				},
			];
			setDashboardData(refinedResponseData);
		}
	}, [adminDashboardResponse, t]);

	useEffect(() => {
		void getDashboardData();
	}, [getDashboardData]);

	const handleFilterPopup = () => {
		setIsFilterModalOpen(true);
	};

	const handleFilterModalClose = () => {
		setIsFilterModalOpen(false);
	};

	const handleResetFilters = useCallback(() => {
		onFilterChange({
			status: [
				ApplicationStatus.FUNCTIONARY_ASSIGNED,
				ApplicationStatus.FUNCTIONARY_SUBMITTED,
				ApplicationStatus.WAITING_FOR_CARE_TEAM,
				ApplicationStatus.APPROVED,
				ApplicationStatus.REJECTED,
				ApplicationStatus.USER_SUBMITTED_APPLICATION,
			],
		});
		onDashboardFilterChange({});
		setSelectedCardIndex(0);
	}, [onFilterChange, onDashboardFilterChange, setSelectedCardIndex]);

	return (
		<>
			<div className="md:hidden flex justify-center items-center pt-3 pb-1 relative">
				<Text className="font-sans font-bold text-lg mb-1">
					{t('adminDashboard')}
				</Text>
			</div>
			<div className="flex justify-between items-center pt-1 pb-3 px-5 md:px-5 md:py-5 relative">
				{/* Back Button - Desktop */}
				<Button
					type="primary"
					onClick={handleBackFunctionaryClick}
					className="hidden md:block font-bold text-sm"
				>
					<span className="flex gap-2">
						<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
						{t('back')}
					</span>
				</Button>

				{/* Back Button + Title - Mobile */}
				<div className="block sm:hidden">
					<a
						onClick={handleBackFunctionaryClick}
						className="relative top:0 md:top-[3px] mr-2 font-bold text-sm"
					>
						<img src={LeftArrowBlack} width={'w-4'} height={'h-4'} />
					</a>
				</div>

				{/* Centered Title */}
				<Text className="hidden md:block font-sans font-bold text-xl mb-1 absolute left-1/2 transform -translate-x-1/2">
					{t('adminDashboard')}
				</Text>

				{/* Filter Button - Desktop */}
				<div
					className={`flex ${
						isLengthyLanguage ? 'flex-col-reverse gap-2' : 'flex-row'
					}`}
				>
					{isFilterApplied ? (
						<Button
							className="hidden sm:block flex-1 h-9 text-red-500 font-sans font-bold"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
					) : (
						''
					)}
					<Button
						className={`hidden md:block text-primary-blue font-sans font-bold text-sm border border-solid mt-0.5`}
						variant="outlined"
						onClick={handleFilterPopup}
					>
						{isFilterApplied ? t('filterApplied') : t('filter')}
					</Button>
				</div>

				{/* Filter Icon - Mobile */}
				<Button
					className="block sm:hidden"
					variant="outlined"
					onClick={handleFilterPopup}
				>
					<img src={FilterIcon} alt="Filter Icon" className="w-5 h-5" />
				</Button>
			</div>
			<div className="flex flex-wrap gap-4 pt-2 pb-5 px-5 md:py-5 md:px-5">
				{dashboardData.map((data, index) => (
					<div
						key={index}
						className="flex-1 basis-[calc(50%-0.5rem)] max-w-[calc(50%-0.5rem)] lg:basis-[calc(25%-0.75rem)] lg:max-w-[calc(25%-0.75rem)]"
					>
						<div
							className={`rounded-lg border-solid px-2 md:px-4 py-2 h-full ${
								selectedCardIndex === index
									? 'border-primary-blue'
									: data.count !== 0
									? 'border-gray-200'
									: 'border-gray-300'
							} ${
								data.count !== 0
									? 'bg-card-bg md:bg-white cursor-pointer'
									: 'bg-gray-200 cursor-not-allowed'
							}`}
							onClick={
								data.count !== 0 ? () => handleCardClick(index) : undefined
							}
						>
							<div className="flex gap-3 md:gap-6 items-center min-h-[100px]">
								<div className="flex-shrink-0">
									<img
										src={data.imageSrc}
										className="w-[38px] md:w-[50px] h-[38px] md:h-[50px]"
									/>
								</div>

								<div className="flex flex-col justify-center">
									<Text
										className={`text-sm md:text-lg leading-normal font-bold m-0 ${
											data.count !== 0 ? 'text-black' : 'text-secondary-gray'
										}`}
									>
										{data.count}
									</Text>
									<Text
										className={`text-[12px] md:text-[16px] m-0 ${
											data.count !== 0 ? 'text-gray-600' : 'text-secondary-gray'
										}`}
									>
										{data.description}
									</Text>
								</div>
							</div>
						</div>
					</div>
				))}
			</div>
			<div className="pt-2 pb-5 px-5 md:py-5 md:px-5">
				<DashboardList
					data={applicationsList}
					onEvaluateClick={handleEvaluateClick}
					onVerifyResultsClick={handleVerifyResultsClick}
					onPageChange={onPageChange}
					pageConfig={paging}
					overAllCandidateStatus={overallCandidates}
					isZCUnpublishedRecords={isZCUnpublishedRecords}
				/>
				<VerifyResultsModal
					openModal={openResultsModal}
					onClose={() => setOpenResultsModal(false)}
					application={applicationToVerify}
				/>
			</div>
			<FilterDashboard
				open={isFilterModalOpen}
				onClose={handleFilterModalClose}
				filterValue={dashboardFilter}
				onApplicationFilterChange={onDashboardFilterChange}
			/>
		</>
	);
};
